import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Search, User, Phone, Mail, Building, 
  Star, StarOff, ChevronRight, Camera, ScanLine, Loader2
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import AddContactDialog from './AddContactDialog';
import ContactDetailSheet from './ContactDetailSheet';
import ScanBusinessCardDialog from './ScanBusinessCardDialog';
import { useDemo } from '@/contexts/DemoContext';

const CATEGORY_COLORS = {
  personal: { bg: 'bg-blue-100', text: 'text-blue-700' },
  work: { bg: 'bg-purple-100', text: 'text-purple-700' },
  medical: { bg: 'bg-green-100', text: 'text-green-700' },
  emergency: { bg: 'bg-red-100', text: 'text-red-700' },
  service: { bg: 'bg-amber-100', text: 'text-amber-700' },
  other: { bg: 'bg-slate-100', text: 'text-slate-700' },
};

const ContactsTab = () => {
  const { isDemoMode, getContacts } = useDemo();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, [isDemoMode]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const demoContacts = getContacts() || [];
        setContacts(demoContacts);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/contacts');
      setContacts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      if (!isDemoMode) {
        toast.error('Failed to load contacts');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactAdded = () => {
    fetchContacts();
    setShowAddDialog(false);
    setShowScanDialog(false);
    toast.success('Contact added successfully');
  };

  const handleContactUpdated = () => {
    fetchContacts();
    setSelectedContact(null);
    toast.success('Contact updated');
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Remove this contact? You can always add them again later.')) return;
    try {
      await API.delete(`/contacts/${contactId}`);
      fetchContacts();
      setSelectedContact(null);
      toast.success('Contact removed');
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const handleToggleFavorite = async (contact) => {
    try {
      await API.put(`/contacts/${contact.id}`, {
        is_favorite: !contact.is_favorite
      });
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.email && contact.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (contact.phone && contact.phone.includes(searchQuery)) ||
      (contact.company && contact.company.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = filterCategory === 'all' || contact.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Sort: favorites first, then alphabetically
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contacts-tab">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={() => setShowScanDialog(true)}
          variant="outline"
          className="border-pink-200 text-pink-700 hover:bg-pink-50"
          data-testid="scan-business-card-btn"
        >
          <ScanLine className="h-4 w-4 mr-2" />
          Scan Business Card
        </Button>
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="bg-pink-500 hover:bg-pink-600"
          data-testid="add-contact-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Contact Manually
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white/50"
            data-testid="search-contacts"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 bg-white/50 text-sm"
          data-testid="filter-category"
        >
          <option value="all">All Categories</option>
          <option value="personal">Personal</option>
          <option value="work">Work</option>
          <option value="medical">Medical</option>
          <option value="emergency">Emergency</option>
          <option value="service">Service</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Contacts List */}
      {sortedContacts.length === 0 ? (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium mb-2">Ready to Add Contacts</p>
          <p className="text-sm text-slate-400 mb-4">
            Scan a business card or add contacts manually to get started
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button 
              onClick={() => setShowScanDialog(true)}
              variant="outline"
            >
              <ScanLine className="h-4 w-4 mr-2" />
              Scan Card
            </Button>
            <Button 
              onClick={() => setShowAddDialog(true)}
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Manually
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {sortedContacts.map((contact) => {
            const categoryColor = CATEGORY_COLORS[contact.category] || CATEGORY_COLORS.other;
            return (
              <Card 
                key={contact.id}
                className="cursor-pointer hover:shadow-md transition-shadow border-pink-100/50"
                onClick={() => setSelectedContact(contact)}
                data-testid={`contact-card-${contact.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-semibold text-pink-700">
                        {contact.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-800 truncate">{contact.name}</h4>
                        {contact.is_favorite && (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      
                      {contact.company && (
                        <p className="text-sm text-slate-500 truncate flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {contact.company}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge className={`${categoryColor.bg} ${categoryColor.text} text-xs`}>
                          {contact.category}
                        </Badge>
                        
                        {contact.phone && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                        
                        {contact.email && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[150px]">{contact.email}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(contact);
                        }}
                        className="p-2"
                      >
                        {contact.is_favorite ? (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        ) : (
                          <StarOff className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AddContactDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onContactAdded={handleContactAdded}
      />

      <ScanBusinessCardDialog
        open={showScanDialog}
        onOpenChange={setShowScanDialog}
        onContactAdded={handleContactAdded}
      />

      <ContactDetailSheet
        contact={selectedContact}
        open={!!selectedContact}
        onOpenChange={(open) => !open && setSelectedContact(null)}
        onContactUpdated={handleContactUpdated}
        onContactDeleted={() => handleDeleteContact(selectedContact?.id)}
      />
    </div>
  );
};

export default ContactsTab;
